import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Loader2, Shield } from 'lucide-react';
type GuardStatus = 'loading' | 'authorized' | 'unauthenticated' | 'forbidden';
export function RequirePlatformAdmin({
  children
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const [status, setStatus] = useState<GuardStatus>('loading');
  useEffect(() => {
    let cancelled = false;
    async function checkAccess() {
      console.log('[RequirePlatformAdmin] Checking access for:', location.pathname);
      // 1. Authenticate user (server-validated)
      const {
        data: {
          user
        },
        error: userError
      } = await supabase.auth.getUser();
      if (cancelled) return;
      if (userError || !user) {
        console.log('[RequirePlatformAdmin] No authenticated user — redirecting to /admin/login', {
          userError
        });
        setStatus('unauthenticated');
        return;
      }
      console.log('[RequirePlatformAdmin] Authenticated user:', {
        id: user.id,
        email: user.email,
        app_metadata: user.app_metadata,
        user_metadata: user.user_metadata
      });
      // 2. Check platform_admins table first (primary source)
      try {
        const {
          data: platformData,
          error: platformError
        } = await supabase.from('platform_admins').select('user_id, role, is_active').eq('user_id', user.id).eq('is_active', true).maybeSingle();
        console.log('[RequirePlatformAdmin] platform_admins query result:', {
          data: platformData,
          error: platformError?.message || null,
          code: platformError?.code || null
        });
        if (cancelled) return;
        if (!platformError && platformData && (platformData.role === 'super_admin' || platformData.role === 'platform_admin')) {
          console.log('[RequirePlatformAdmin] ✅ Access granted via platform_admins:', {
            email: user.email,
            role: platformData.role
          });
          setStatus('authorized');
          return;
        }
      } catch (err) {
        console.warn('[RequirePlatformAdmin] platform_admins check exception:', err);
      }
      // 3. Fallback: check restaurant_users table
      try {
        const {
          data: roleData,
          error: roleError
        } = await supabase.from('restaurant_users').select('role').eq('user_id', user.id).eq('is_active', true).in('role', ['super_admin', 'platform_admin']).limit(1).maybeSingle();
        console.log('[RequirePlatformAdmin] restaurant_users query result:', {
          data: roleData,
          error: roleError?.message || null,
          code: roleError?.code || null
        });
        if (cancelled) return;
        if (!roleError && roleData) {
          console.log('[RequirePlatformAdmin] ✅ Access granted via restaurant_users:', {
            email: user.email,
            role: roleData.role
          });
          setStatus('authorized');
          return;
        }
      } catch (err) {
        console.warn('[RequirePlatformAdmin] restaurant_users check exception:', err);
      }
      // 4. Last fallback: check user.app_metadata for role (bypasses RLS)
      const metaRole = user.app_metadata?.role || user.app_metadata?.platform_role || user.user_metadata?.role;
      console.log('[RequirePlatformAdmin] app_metadata role fallback:', {
        metaRole
      });
      if (metaRole === 'super_admin' || metaRole === 'platform_admin') {
        console.log('[RequirePlatformAdmin] ✅ Access granted via app_metadata:', {
          email: user.email,
          role: metaRole
        });
        setStatus('authorized');
        return;
      }
      console.log('[RequirePlatformAdmin] ❌ User lacks platform role — forbidden. ' + 'If you believe this is wrong, check RLS policies on platform_admins and restaurant_users tables. ' + 'The user must have a SELECT policy allowing them to read their own row.');
      setStatus('forbidden');
    }
    checkAccess();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);
  if (status === 'loading') {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-1138">
        <div className="text-center space-y-4" data-id="element-1139">
          <div className="w-16 h-16 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center mx-auto" data-id="element-1140">
            <Shield className="w-8 h-8 text-[#006847]" data-id="element-1141" />
          </div>
          <Loader2 className="w-6 h-6 text-[#006847] animate-spin mx-auto" data-id="element-1142" />
          <p className="text-white/40 text-sm" data-id="element-1143">Verificando permisos...</p>
        </div>
      </div>;
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/admin/login" state={{
      from: location.pathname
    }} replace data-id="element-1144" />;
  }
  if (status === 'forbidden') {
    return <Navigate to="/" replace data-id="element-1145" />;
  }
  return <>{children}</>;
}